import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import { apiJson } from "../utils/api";
import useSeo from "../hooks/useSeo";
import RelationshipFeedbackForm from "../components/RelationshipFeedbackForm";
import AiModuleTabs from "../components/AiModuleTabs";

const LAYER_ORDER = [
  "foundation",
  "marriage_values",
  "lifestyle",
  "relationship_mode",
  "sensitive_zones",
];

/**
 * AI 关系地图总览（登录工具页）
 * SEO：noindex，避免与公开营销页重复收录；不改变 /love-map 等已收录 URL。
 */
export default function RelationshipMapPage() {
  const navigate = useNavigate();
  const userId = Number(localStorage.getItem("user_id"));
  const [map, setMap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [editKey, setEditKey] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editNote, setEditNote] = useState("");

  useSeo({
    title: "我的AI关系地图 | AI婚恋智能体",
    metaDescription:
      "登录后查看个人五层关系地图：基础画像、婚姻价值观、生活方式、关系模式与敏感区。",
    path: "/ai/relationship-map",
    noindex: true,
  });

  useEffect(() => {
    if (!userId) {
      navigate(
        `/login?redirect=${encodeURIComponent("/ai/relationship-map")}`,
        { replace: true }
      );
    }
  }, [userId, navigate]);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError("");
    try {
      const { res, data } = await apiJson(
        `/api/ai/relationship-map?user_id=${userId}`
      );
      if (!res.ok) throw new Error(data?.detail || "加载失败");
      setMap(data);
    } catch (err) {
      setError(err.message || "加载失败");
      setMap(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const rebuild = async () => {
    setBusy(true);
    try {
      const { res, data } = await apiJson("/api/ai/relationship-map/rebuild", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });
      if (!res.ok) throw new Error(data?.detail || "刷新失败");
      setMap(data);
    } catch (err) {
      alert(err.message || "刷新失败");
    } finally {
      setBusy(false);
    }
  };

  const saveOverride = async () => {
    if (!editKey || !editValue.trim()) return;
    setBusy(true);
    try {
      const { res, data } = await apiJson("/api/ai/relationship-map/override", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          trait_key: editKey,
          value: editValue.trim(),
          note: editNote.trim(),
        }),
      });
      if (!res.ok) throw new Error(data?.detail || "保存失败");
      setMap(data);
      setEditKey(null);
      setEditValue("");
      setEditNote("");
    } catch (err) {
      alert(err.message || "保存失败");
    } finally {
      setBusy(false);
    }
  };

  const clearOverride = async (key) => {
    setBusy(true);
    try {
      const { res, data } = await apiJson(
        "/api/ai/relationship-map/clear-override",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: userId, trait_key: key }),
        }
      );
      if (!res.ok) throw new Error(data?.detail || "撤销失败");
      setMap(data);
    } catch (err) {
      alert(err.message || "撤销失败");
    } finally {
      setBusy(false);
    }
  };

  const sources = map?.sources || {};
  const layers = map?.layers || {};

  return (
    <div className="min-h-screen bg-[#f6f3ee] flex flex-col">
      <Header />
      <main className="flex-1 w-full mx-auto px-4 py-8 max-w-5xl space-y-6">
        <AiModuleTabs groupKey="relationship" />
        <section className="rounded-3xl bg-gradient-to-br from-rose-50 via-violet-50 to-sky-50 border border-white p-6 md:p-8 space-y-3">
          <p className="text-sm text-violet-600 font-medium">AI 婚恋智能体 · 关系地图</p>
          <h1 className="text-3xl font-extrabold text-gray-900">我的 AI 关系地图</h1>
          <p className="text-sm text-gray-600 max-w-2xl leading-relaxed">
            先认识自己，再理解关系。这里把画像、爱情地图与授权陪伴汇成五层观察——会随你的互动更新，而不是一次性 PDF。
          </p>
          {map?.headline && (
            <p className="text-sm text-violet-900 font-medium">{map.headline}</p>
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            <button
              type="button"
              disabled={busy}
              onClick={rebuild}
              className="px-3 py-1.5 rounded-full bg-violet-600 text-white text-xs disabled:opacity-50"
            >
              重新聚合
            </button>
            <Link
              to="/ai/consultant"
              className="px-3 py-1.5 rounded-full bg-white border text-xs"
            >
              AI 婚恋画像
            </Link>
            <Link
              to="/love-map"
              className="px-3 py-1.5 rounded-full bg-white border text-xs"
            >
              AI 爱情地图
            </Link>
            <Link
              to="/ai/simulation"
              className="px-3 py-1.5 rounded-full bg-white border text-xs"
            >
              关系模拟
            </Link>
          </div>
        </section>

        {loading && (
          <p className="text-center text-sm text-gray-500 py-10">加载中…</p>
        )}
        {error && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {error}
          </div>
        )}

        {map && !loading && (
          <>
            <section className="rounded-2xl bg-white border p-5 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-semibold text-gray-900">完整度</h2>
                <span className="text-violet-700 font-bold tabular-nums">
                  {map.overall_completeness ?? 0}%
                </span>
              </div>
              <div className="h-2 rounded-full bg-violet-100 overflow-hidden">
                <div
                  className="h-full bg-violet-500 transition-all"
                  style={{
                    width: `${Math.min(100, map.overall_completeness || 0)}%`,
                  }}
                />
              </div>
              {map.agent_tagline && (
                <p className="text-xs text-violet-700 font-medium">
                  {map.agent_tagline}
                </p>
              )}
              {(map.agent_loop || []).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {(map.agent_loop || []).map((step, idx) => (
                    <Link
                      key={step.key}
                      to={step.path || "/ai/relationship-map"}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] border ${
                        step.done
                          ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                          : "border-gray-100 bg-gray-50 text-gray-500"
                      }`}
                    >
                      <span className="opacity-50">{idx + 1}</span>
                      {step.label}
                    </Link>
                  ))}
                </div>
              )}
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
                {Object.entries(sources).map(([k, s]) => (
                  <Link
                    key={k}
                    to={s.path || "#"}
                    className={`rounded-xl border px-3 py-2 text-xs ${
                      s.done
                        ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                        : "border-gray-100 bg-gray-50 text-gray-600"
                    }`}
                  >
                    <span className="font-medium">
                      {s.done ? "已接入 · " : "待补充 · "}
                      {s.label}
                    </span>
                  </Link>
                ))}
              </div>
            </section>

            {(map.learned_from_feedback || []).length > 0 && (
              <section className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4 space-y-2">
                <p className="text-sm font-semibold text-emerald-900">
                  AI 已根据你的反馈更新了观察
                </p>
                <ul className="space-y-1.5">
                  {(map.learned_from_feedback || []).map((item) => (
                    <li key={item.id} className="text-xs text-emerald-900/90">
                      · {item.summary}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <RelationshipFeedbackForm
              userId={userId}
              source="dating"
              onSubmitted={() => load()}
            />

            {LAYER_ORDER.map((key) => {
              const layer = layers[key];
              if (!layer) return null;
              const meta = layer.meta || {};
              return (
                <section
                  key={key}
                  className="rounded-2xl bg-white border p-5 space-y-3"
                >
                  <div className="flex flex-wrap justify-between gap-2">
                    <div>
                      <h2 className="font-semibold text-gray-900">
                        {meta.title || key}
                      </h2>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {meta.desc}
                      </p>
                    </div>
                    <span className="text-[11px] text-violet-600">
                      完整度 {layer.completeness ?? 0}%
                    </span>
                  </div>
                  <ul className="space-y-2">
                    {(layer.traits || []).map((t) => (
                      <li
                        key={t.key}
                        className="rounded-xl border border-gray-100 px-3 py-2.5"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-xs text-gray-400">
                              {t.label}
                              {t.overridden ? " · 已手动修正" : ""}
                              <span className="ml-1">[{t.source}]</span>
                            </p>
                            <p className="text-sm text-gray-800 mt-0.5 leading-relaxed">
                              {t.value}
                            </p>
                          </div>
                          {t.editable && (
                            <button
                              type="button"
                              className="text-[11px] text-violet-600 shrink-0"
                              onClick={() => {
                                setEditKey(t.key);
                                setEditValue(t.value || "");
                                setEditNote("");
                              }}
                            >
                              修正
                            </button>
                          )}
                          {t.overridden && (
                            <button
                              type="button"
                              className="text-[11px] text-gray-500 shrink-0"
                              onClick={() => clearOverride(t.key)}
                            >
                              撤销修正
                            </button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}

            {editKey && (
              <div className="fixed inset-0 z-50 bg-black/30 flex items-end sm:items-center justify-center p-4">
                <div className="bg-white rounded-2xl w-full max-w-md p-5 space-y-3 shadow-xl">
                  <h3 className="font-semibold text-gray-900">修正这条观察</h3>
                  <p className="text-xs text-gray-500">
                    只改结构化描述，不会把聊天原文公开给他人。
                  </p>
                  <textarea
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value.slice(0, 300))}
                    rows={3}
                    className="w-full border rounded-xl px-3 py-2 text-sm"
                  />
                  <input
                    value={editNote}
                    onChange={(e) => setEditNote(e.target.value.slice(0, 200))}
                    placeholder="可选：修正说明"
                    className="w-full border rounded-xl px-3 py-2 text-sm"
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      className="px-3 py-1.5 text-sm border rounded-full"
                      onClick={() => setEditKey(null)}
                    >
                      取消
                    </button>
                    <button
                      type="button"
                      disabled={busy || !editValue.trim()}
                      className="px-3 py-1.5 text-sm rounded-full bg-violet-600 text-white disabled:opacity-50"
                      onClick={saveOverride}
                    >
                      保存
                    </button>
                  </div>
                </div>
              </div>
            )}

            <p className="text-[11px] text-gray-400 leading-relaxed">
              {map.disclaimer}
            </p>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
